import type {Employee} from "../../domain/entities/employee.entity";
import {resolveUserDisplayName} from "./user-display-name.helper";
import {isPageLeaving} from '../../helpers/page-leaving';

export interface UserDisplayNameDeps {
    isLoggedIn: () => boolean;
    getUsername: () => string | null;
    getEmployee: () => Promise<Employee | null>;
    onName: (name: string) => void;
}

/**
 * Keeps the sidebar's user-name label in step with the logged-in user
 * (Anke, 2026-09-18) — pulled out of `ha-app.component.ts` so the async
 * ordering rules are testable without Lit or a DOM (review 2026-09-19, G6).
 *
 * Logged out → cleared. Overlapping `refresh()` calls (initial hydrate + the
 * `user` listener, or a fast user switch) are resolved by `generation`: only
 * the newest call may write, so an older, slower answer can never overwrite
 * the current user's name with a stale one. The username is read BEFORE the
 * await for the same reason — never mix one user's Employee with another's
 * username.
 */
export class UserDisplayNameController {

    private generation = 0;

    public constructor(private readonly deps: UserDisplayNameDeps) {
    }

    public async refresh(): Promise<void> {
        const generation = ++this.generation;
        if (!this.deps.isLoggedIn()) {
            this.deps.onName('');
            return;
        }

        const username = this.deps.getUsername();
        let name: string;
        try {
            name = resolveUserDisplayName(await this.deps.getEmployee(), username);
        } catch (error) {
            // A reload or navigation cancels this request mid-flight, and the browser reports that
            // as `TypeError: Failed to fetch`. That is not a failure the user should see logged; a
            // real network or server failure while the page stays still is. Same distinction, same
            // shared flag as the timeline's start cursor (37704775) — measured there that `pagehide`
            // fires before the cancelled fetch rejects, so the flag is already set when we arrive here.
            // Without it, seven of eleven routes in smoke.spec.ts went red on a cancellation with
            // nothing actually broken: the fallback below had already produced a usable name.
            if (!isPageLeaving()) {
                console.error(`Failed to resolve the logged-in user's display name.`, error);
            }
            name = username ?? '';
        }
        if (generation === this.generation) {
            this.deps.onName(name);
        }
    }
}
